import { normalizeNumber } from "web-apps/company/components/common/number.normalize.logic";

export const DEFAULT_TZ = "Europe/Stockholm";
export const NONE_ABSENCE_VALUE = "None";
export const DEFAULT_START_TIME = "08:00:00";
export const DEFAULT_END_TIME = "17:00:00";

const pad2 = (n: number): string => (n < 10 ? `0${n}` : String(n));

const normalizeTimeParts = (
  hours: number,
  minutes: number,
  seconds: number,
): string | null => {
  const hasInvalidHours = hours < 0 || hours > 23;
  const hasInvalidMinutes = minutes < 0 || minutes > 59;
  const hasInvalidSeconds = seconds < 0 || seconds > 59;

  if (hasInvalidHours || hasInvalidMinutes || hasInvalidSeconds) {
    return null;
  }

  return `${pad2(hours)}:${pad2(minutes)}:${pad2(seconds)}`;
};

const extractTimeParts = (value: string): string | null => {
  const directMatch = value.match(/^(\d{1,2}):(\d{2})(?::(\d{2}))?$/);
  if (directMatch) {
    return normalizeTimeParts(
      Number(directMatch[1]),
      Number(directMatch[2]),
      Number(directMatch[3] ?? "0"),
    );
  }

  const isoMatch = value.match(/T(\d{2}):(\d{2})(?::(\d{2}))?/);
  if (isoMatch) {
    return normalizeTimeParts(
      Number(isoMatch[1]),
      Number(isoMatch[2]),
      Number(isoMatch[3] ?? "0"),
    );
  }

  return null;
};

export const normalizeBackendTime = (
  value: string | undefined,
  fallback: string,
): string => {
  const trimmedValue = value?.trim();
  if (!trimmedValue) return fallback;

  return extractTimeParts(trimmedValue) ?? fallback;
};

export const toTimePickerValue = (
  value: string | undefined,
  fallback: string,
): string => normalizeBackendTime(value, fallback).slice(0, 5);

export const fromTimePickerValue = (
  value: string,
  fallback: string,
): string => {
  const trimmedValue = value.trim();
  if (!trimmedValue) return fallback;

  const match = trimmedValue.match(/^(\d{1,2}):(\d{2})$/);
  if (!match) return fallback;

  return normalizeTimeParts(Number(match[1]), Number(match[2]), 0) ?? fallback;
};

export const decimalToPercent = (
  decimal: number | null | undefined,
  fallbackPercent: number,
): number => {
  if (decimal === null || decimal === undefined) return fallbackPercent;
  return decimal * 100;
};

export const percentToDecimalString = (
  percent: number | null | undefined,
  fallbackPercent: number,
): string => {
  const normalizedPercent = (percent ?? fallbackPercent) / 100;
  const roundedValue = Math.round(normalizedPercent * 1e6) / 1e6;
  return String(roundedValue);
};

const toStringArray = (value: string | undefined): string[] => {
  const trimmedValue = value?.trim();
  if (!trimmedValue) return [];

  try {
    const parsedValue = JSON.parse(trimmedValue);
    if (Array.isArray(parsedValue)) {
      return parsedValue.map((entry) => String(entry));
    }
  } catch {}

  return trimmedValue
    .split(",")
    .map((entry) => entry.trim())
    .filter(Boolean);
};

const enforceNoneRules = (value: string[]): string[] => {
  const uniqueValues = Array.from(new Set(value.filter(Boolean).map(String)));

  if (uniqueValues.length === 0) return [NONE_ABSENCE_VALUE];

  const hasNonNone = uniqueValues.some((entry) => entry !== NONE_ABSENCE_VALUE);
  return hasNonNone
    ? uniqueValues.filter((entry) => entry !== NONE_ABSENCE_VALUE)
    : [NONE_ABSENCE_VALUE];
};

export const arrayToStringForBackend = (value: string[]): string =>
  enforceNoneRules(value).join(", ");

export const stringNormalize = (value: string): string =>
  arrayToStringForBackend(toStringArray(value));

export const toBool = (
  value: string | undefined,
  fallback = false,
): boolean => {
  const trimmedValue = value?.trim().toLowerCase();
  if (!trimmedValue) return fallback;
  return trimmedValue === "true" || trimmedValue === "1";
};

export const toNumber = (value: string | undefined, fallback = 0): number =>
  normalizeNumber(value ?? "") ?? fallback;
